import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../auth/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import {
  Loader2, Plus, Users, Building2, ChevronRight, Power, PowerOff,
  Infinity as InfinityIcon, RotateCcw, Trash2, Save, Link2, UserPlus,
} from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => (n ?? 0).toLocaleString();

export default function Admin() {
  const { user, workspaceOrg, setWorkspaceOrg } = useAuth();
  const [data, setData] = useState({ orgs: [], users: [] });
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState({ org_name: "", email: "", password: "", name: "" });
  const [submitting, setSubmitting] = useState(false);
  const [limitEdits, setLimitEdits] = useState({});
  const [busy, setBusy] = useState({});
  const [memberFor, setMemberFor] = useState(null); // org object or null
  const [memberForm, setMemberForm] = useState({ name: "", email: "", password: "" });
  const [memberBusy, setMemberBusy] = useState(false);

  const submitMember = async (e) => {
    e.preventDefault();
    if (!memberFor) return;
    setMemberBusy(true);
    try {
      await axios.post(`/admin/orgs/${memberFor.id}/members`, memberForm);
      toast.success(`Login "${memberForm.email}" added to ${memberFor.name}`);
      setMemberFor(null);
      setMemberForm({ name: "", email: "", password: "" });
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not add member");
    } finally { setMemberBusy(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`/admin/orgs`);
      setData(data);
      setLimitEdits({});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load workspaces");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (user?.role !== "super_admin") {
    return <div className="max-w-5xl mx-auto px-6 py-16 text-slate-500 text-sm">Admin access required.</div>;
  }

  const usersByOrg = data.users.reduce((acc, u) => {
    (acc[u.org_id] = acc[u.org_id] || []).push(u); return acc;
  }, {});

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`/admin/orgs`, form);
      toast.success(`Workspace "${form.org_name}" created`);
      setOpenCreate(false);
      setForm({ org_name: "", email: "", password: "", name: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Create failed");
    } finally { setSubmitting(false); }
  };

  const patchOrg = async (orgId, body, msg) => {
    setBusy((b) => ({ ...b, [orgId]: true }));
    try {
      await axios.patch(`/admin/orgs/${orgId}`, body);
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally { setBusy((b) => ({ ...b, [orgId]: false })); }
  };

  const deleteOrg = async (org) => {
    if (!window.confirm(`Delete "${org.name}" and ALL of its reports/users/data? This cannot be undone.`)) return;
    setBusy((b) => ({ ...b, [org.id]: true }));
    try {
      await axios.delete(`/admin/orgs/${org.id}`);
      toast.success(`Workspace "${org.name}" deleted`);
      if (workspaceOrg === org.id) setWorkspaceOrg("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally { setBusy((b) => ({ ...b, [org.id]: false })); }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-10 text-slate-900" data-testid="admin-panel">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-[10px] uppercase font-mono tracking-widest text-pink-600 mb-1">Super-admin</div>
          <h1 className="text-2xl font-semibold">Workspaces</h1>
          <p className="text-xs text-slate-400 mt-1">
            Full isolation between workspaces. Set a URL-fetch limit, disable access instantly, or delete a workspace and every trace of its data.
          </p>
        </div>
        <Button onClick={() => setOpenCreate(true)} className="bg-slate-900 hover:bg-slate-800 text-white font-semibold" data-testid="admin-new-client-btn">
          <Plus size={13} className="mr-1.5" /> New Client
        </Button>
      </div>

      {loading ? (
        <div className="text-slate-400 text-sm font-mono">Loading workspaces…</div>
      ) : (
        <div className="space-y-3" data-testid="admin-orgs-list">
          {data.orgs.map((o) => {
            const active = workspaceOrg === o.id || (!workspaceOrg && o.id === "canon-internal");
            const disabled = !!o.disabled;
            const isDefault = o.id === "canon-internal";
            const limitValue = limitEdits[o.id] ?? (o.links_limit == null ? "" : String(o.links_limit));
            const usagePct = o.links_limit ? Math.min(100, Math.round((o.links_used / o.links_limit) * 100)) : 0;
            const overLimit = o.links_limit != null && o.links_used >= o.links_limit;
            return (
              <div key={o.id}
                className={`rounded-xl border p-5 transition-colors ${
                  disabled ? "border-red-200 bg-red-50" :
                  active ? "border-pink-300 bg-pink-50" : "border-slate-200 bg-white hover:border-slate-300"
                }`}
                data-testid={`admin-org-${o.id}`}
              >
                {/* Header row: name + status + actions */}
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className={`p-2 rounded-lg border ${disabled ? "bg-red-50 border-red-200" : "bg-white border-slate-200"}`}>
                      <Building2 size={16} className={disabled ? "text-red-600" : "text-pink-600"} />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
                        {o.name}
                        {disabled && (
                          <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-red-100 text-red-600">
                            Disabled
                          </span>
                        )}
                        {active && !disabled && (
                          <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-pink-100 text-pink-600">Viewing</span>
                        )}
                        {isDefault && (
                          <span className="text-[9px] font-mono uppercase tracking-widest text-slate-400">Default</span>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-400 font-mono mt-1 flex items-center gap-3 flex-wrap">
                        <span>{o.report_count} reports</span>
                        <span className="text-slate-300">·</span>
                        <span className="flex items-center gap-1"><Users size={10} /> {o.user_count} user(s)</span>
                        {(usersByOrg[o.id] || []).slice(0, 2).map((u) => (
                          <span key={u.id} className="text-slate-400 truncate max-w-[200px]">{u.email}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => patchOrg(o.id, { disabled: !disabled }, disabled ? `Enabled ${o.name}` : `Disabled ${o.name}`)}
                      disabled={busy[o.id] || isDefault}
                      variant="outline"
                      className={`text-xs h-8 border ${disabled
                        ? "bg-emerald-50 border-emerald-200 text-emerald-600 hover:bg-emerald-100"
                        : "bg-white border-slate-200 hover:bg-red-50 hover:border-red-200 hover:text-red-600 text-slate-700"}`}
                      title={isDefault ? "The default workspace cannot be disabled" : (disabled ? "Enable this workspace" : "Disable access immediately")}
                      data-testid={`admin-toggle-${o.id}`}
                    >
                      {disabled ? <><Power size={11} className="mr-1" /> Enable</> : <><PowerOff size={11} className="mr-1" /> Disable</>}
                    </Button>
                    <Button
                      onClick={() => setWorkspaceOrg(active ? "" : o.id)}
                      variant="outline"
                      className="bg-white border-slate-200 hover:bg-pink-50 hover:border-pink-300 hover:text-pink-600 text-slate-700 text-xs h-8"
                      data-testid={`admin-switch-${o.id}`}
                    >
                      {active ? "Exit view" : (<>View as <ChevronRight size={11} className="ml-1" /></>)}
                    </Button>
                    <Button
                      onClick={() => { setMemberFor(o); setMemberForm({ name: "", email: "", password: "" }); }}
                      variant="outline"
                      className="bg-white border-slate-200 hover:bg-pink-50 hover:border-pink-300 hover:text-pink-600 text-slate-700 text-xs h-8"
                      title="Allocate an extra team-member login to this workspace"
                      data-testid={`admin-add-member-${o.id}`}
                    >
                      <UserPlus size={11} className="mr-1" /> Add login
                    </Button>
                    {!isDefault && (
                      <Button
                        onClick={() => deleteOrg(o)}
                        disabled={busy[o.id]}
                        variant="outline"
                        className="bg-white border-slate-200 hover:bg-red-50 hover:border-red-200 hover:text-red-600 text-slate-400 text-xs h-8 w-8 p-0"
                        title="Delete workspace and all its data"
                        data-testid={`admin-delete-${o.id}`}
                      >
                        <Trash2 size={12} />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Quota row */}
                <div className="mt-4 pt-4 border-t border-slate-200/60 grid md:grid-cols-[1fr_auto] gap-4 items-center">
                  <div>
                    <div className="text-[10px] uppercase font-mono tracking-widest text-slate-400 mb-1.5 flex items-center gap-1.5">
                      <Link2 size={11} className="text-pink-600" />
                      URL Fetch Usage
                    </div>
                    {o.links_limit == null ? (
                      <div className="flex items-center gap-2 text-xs text-slate-500 font-mono">
                        <InfinityIcon size={14} className="text-emerald-600" />
                        Unlimited · used <span className="text-slate-800 font-semibold">{fmt(o.links_used)}</span>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center justify-between text-xs mb-1.5 font-mono">
                          <span className={overLimit ? "text-red-600" : "text-slate-700"}>
                            {fmt(o.links_used)} / {fmt(o.links_limit)} links
                          </span>
                          <span className={`text-[10px] ${overLimit ? "text-red-600" : "text-slate-400"}`}>{usagePct}%</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-white overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${
                              overLimit ? "bg-red-500" : usagePct > 80 ? "bg-slate-900" : "bg-emerald-500"
                            }`}
                            style={{ width: `${Math.max(2, usagePct)}%` }}
                          />
                        </div>
                      </>
                    )}
                  </div>
                  <div className="flex items-end gap-2">
                    <div>
                      <label className="text-[10px] uppercase font-mono tracking-widest text-slate-400 mb-1 block">
                        New limit (blank = unlimited)
                      </label>
                      <Input
                        type="number"
                        min="0"
                        value={limitValue}
                        onChange={(e) => setLimitEdits({ ...limitEdits, [o.id]: e.target.value })}
                        className="bg-white border-slate-200 font-mono text-xs h-8 w-32"
                        placeholder="e.g. 5000"
                        data-testid={`admin-limit-input-${o.id}`}
                      />
                    </div>
                    <Button
                      onClick={() => {
                        const raw = limitEdits[o.id];
                        const body = raw === "" || raw == null ? { links_limit: null } : { links_limit: Number(raw) };
                        // FastAPI treats missing = None; but Pydantic Optional[int]=None ignores null in patch,
                        // so blank means "make unlimited" via a special sentinel value = 999999999.
                        // Cleaner: send explicit 0 to block, unlimited handled via a separate button below.
                        if (body.links_limit === null) {
                          toast.info("To make unlimited, click the ∞ button.");
                          return;
                        }
                        if (Number.isNaN(body.links_limit) || body.links_limit < 0) {
                          toast.error("Enter a non-negative number, or click ∞ for unlimited.");
                          return;
                        }
                        patchOrg(o.id, body, `Limit updated to ${body.links_limit}`);
                      }}
                      disabled={busy[o.id]}
                      className="h-8 bg-slate-900 hover:bg-slate-800 text-white font-semibold text-xs disabled:opacity-40"
                      data-testid={`admin-limit-save-${o.id}`}
                    >
                      <Save size={11} className="mr-1" /> Save
                    </Button>
                    <Button
                      onClick={() => patchOrg(o.id, { links_limit: 999999999 }, `${o.name} is now unlimited`)}
                      disabled={busy[o.id]}
                      variant="outline"
                      className="h-8 bg-white border-slate-200 hover:bg-emerald-50 hover:border-emerald-200 hover:text-emerald-600 text-slate-500 text-xs"
                      title="Remove the URL-fetch cap for this workspace"
                      data-testid={`admin-unlimit-${o.id}`}
                    >
                      <InfinityIcon size={12} />
                    </Button>
                    <Button
                      onClick={() => patchOrg(o.id, { reset_usage: true }, `Usage counter reset for ${o.name}`)}
                      disabled={busy[o.id]}
                      variant="outline"
                      className="h-8 bg-white border-slate-200 hover:bg-pink-50 hover:border-pink-300 hover:text-pink-600 text-slate-500 text-xs"
                      title="Reset the used counter to 0 (start a new billing period)"
                      data-testid={`admin-reset-${o.id}`}
                    >
                      <RotateCcw size={12} />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={openCreate} onOpenChange={(o) => !o && setOpenCreate(false)}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md" data-testid="admin-create-dialog">
          <DialogHeader>
            <DialogTitle>New Client Workspace</DialogTitle>
            <DialogDescription className="text-slate-400 text-xs">
              Creates an isolated workspace with a single login. You can set their URL-fetch limit right after creation.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            {[
              { k: "org_name", label: "Workspace name", placeholder: "e.g. Nike Global" },
              { k: "name", label: "Owner display name", placeholder: "Optional" },
              { k: "email", label: "Login email", placeholder: "owner@client.com", type: "email" },
              { k: "password", label: "Password (min 8 chars)", placeholder: "••••••••", type: "password" },
            ].map((f) => (
              <div key={f.k}>
                <label className="text-[10px] uppercase font-mono tracking-widest text-slate-400 mb-1 block">{f.label}</label>
                <Input type={f.type || "text"} value={form[f.k]}
                  onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                  className="bg-white border-slate-200 font-mono text-xs"
                  placeholder={f.placeholder} data-testid={`admin-create-${f.k}`} />
              </div>
            ))}
            <div className="flex items-center justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpenCreate(false)}
                className="bg-white border-slate-200 hover:bg-slate-50 text-slate-700">
                Cancel
              </Button>
              <Button type="submit" disabled={submitting || !form.org_name || !form.email || form.password.length < 8}
                className="bg-slate-900 hover:bg-slate-800 text-white font-semibold disabled:opacity-40"
                data-testid="admin-create-submit">
                {submitting ? (<><Loader2 size={12} className="mr-1.5 animate-spin" /> Creating…</>) : "Create workspace"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!memberFor} onOpenChange={(o2) => !o2 && setMemberFor(null)}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md" data-testid="admin-member-dialog">
          <DialogHeader>
            <DialogTitle>Add login — {memberFor?.name}</DialogTitle>
            <DialogDescription className="text-slate-400 text-xs">
              Creates a team-member login inside this workspace. They sign in on the client portal and see only this workspace's data.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitMember} className="space-y-3">
            {[
              { k: "name", label: "Display name", placeholder: "e.g. Priya Sharma" },
              { k: "email", label: "Login email", placeholder: "teammate@client.com", type: "email" },
              { k: "password", label: "Password (min 8 chars)", placeholder: "••••••••", type: "password" },
            ].map((f) => (
              <div key={f.k}>
                <label className="text-[10px] uppercase font-mono tracking-widest text-slate-400 mb-1 block">{f.label}</label>
                <Input type={f.type || "text"} value={memberForm[f.k]}
                  onChange={(e) => setMemberForm({ ...memberForm, [f.k]: e.target.value })}
                  className="bg-slate-50 border-slate-200 font-mono text-xs text-slate-900"
                  placeholder={f.placeholder} data-testid={`admin-member-${f.k}`} />
              </div>
            ))}
            <div className="flex items-center justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setMemberFor(null)}
                className="bg-white border-slate-200 hover:bg-slate-50 text-slate-700">
                Cancel
              </Button>
              <Button type="submit" disabled={memberBusy || !memberForm.email || memberForm.password.length < 8}
                className="bg-slate-900 hover:bg-slate-800 text-white font-semibold disabled:opacity-40"
                data-testid="admin-member-submit">
                {memberBusy ? (<><Loader2 size={12} className="mr-1.5 animate-spin" /> Adding…</>) : "Add login"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
